import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { AdSetBasicsStep } from './steps/AdSetBasicsStep';
import { AdSetBudgetStep } from './steps/AdSetBudgetStep';
import { AdSetScheduleStep } from './steps/AdSetScheduleStep';
import { AdSetReviewStep } from './steps/AdSetReviewStep';
import { AdSetPromotedObjectStep } from './steps/AdSetPromotedObjectStep';
import { TargetingStep } from './steps/targeting/TargetingStep';
import {
  metaAdsApi,
  type CreateMetaAdSetRequest,
  type MetaAdSetCreateStatus,
  type MetaBidStrategy,
  type MetaBillingEvent,
  type MetaCampaignObjective,
  type MetaOptimizationGoal,
  type MetaPromotedObject,
  type MetaSpecialAdCategory,
  type MetaTargetingSpec,
} from '@/services/api/metaAds';
import {
  AGE_RESTRICTED_CATEGORIES,
  CONSTRAINED_BID_STRATEGIES,
  OBJECTIVE_TO_OPTIMIZATION_GOALS,
  OBJECTIVES_REQUIRING_PROMOTED_OBJECT,
  OPTIMIZATION_GOAL_TO_BILLING_EVENTS,
} from './constants/metaAdSetMatrix';
import type { MetaBudgetMode } from './steps/MetaBudgetStep';
import { debug } from '@/utils/debug';
import { formatDateForInput } from '@/utils/format';
import { linkifyUrls } from '@/utils/linkify';
import { appendOptimisticMetaAdSet } from '@/utils/optimisticMetaCache';
import type { AppConfiguration } from '@/types/api';
import type { Campaign } from '@/types/campaign';

/**
 * CreateAdSetWizard — A4.3.2.
 *
 * Mirrors `CreateMetaCampaignWizard`'s modal frame, cancel-confirm, and
 * responsive footer (the A4.3.1 precedent), but mirrors Google's
 * `CreateAdGroupWizard` for the parent-context posture — campaign id +
 * loaded campaign object flow through props, not through any state in
 * `useWizards`. That mirrors how Google's `wizards.adGroupWizard` mounts
 * (`isOpen / open / close`; the parent component holds the campaign
 * context and passes it as JSX props on render).
 *
 * 6 steps total. Step 5 (PromotedObject) is skipped in nav for objectives
 * that don't require it (`OBJECTIVES_REQUIRING_PROMOTED_OBJECT`).
 */

// eslint-disable-next-line no-control-regex
const NAME_PATTERN = /^[^\x00-\x1f\x7f]+$/;

interface CreateAdSetWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  /** Loaded parent campaign. Carries the meta_ad_account_id, objective,
   *  bid_strategy, and special_ad_categories the wizard needs for UX-side
   *  option filtering. Backend re-validates after lookup. */
  parentCampaign: Campaign;
}

interface FormErrors {
  name?: string;
  optimizationGoal?: string;
  billingEvent?: string;
  amount?: string;
  bidAmount?: string;
  startDate?: string;
  endDate?: string;
  countries?: string;
  age?: string;
  pixel?: string;
  customEventType?: string;
  page?: string;
  applicationId?: string;
  objectStoreUrl?: string;
}

const ALL_WIZARD_STEPS = [
  { label: 'Basics', description: '' },
  { label: 'Budget', description: '' },
  { label: 'Schedule', description: '' },
  { label: 'Targeting', description: '' },
  { label: 'Promoted', description: '' },
  { label: 'Review', description: '' },
];

const defaultTargeting = (): MetaTargetingSpec => ({
  geo_locations: { countries: ['US'] },
  age_min: 18,
  age_max: 65,
  genders: ['MALE', 'FEMALE'],
  interests: [],
  publisher_platforms: [],
  facebook_positions: [],
  instagram_positions: [],
  device_platforms: [],
  locales: [],
  // Advantage Audience defaults off — explicit opt-in for AI-broadened
  // targeting via the Optimization-mode section in TargetingStep.
  advantage_audience: false,
  // Advantage+ Placements defaults TRUE. Two reasons the inconsistent
  // earlier "Manual + empty publisher_platforms" default was wrong:
  //   1. The backend serializer omits empty placement lists, which is
  //      Meta's wire convention for automatic placements — i.e. the
  //      user would silently get Advantage+ Placements anyway, with
  //      the UI lying that they were in Manual mode.
  //   2. A4.3.2 sent the exact same wire shape (empty placement keys),
  //      so this is what "preserve A4.3.2 behaviour" means in practice
  //      — the flag now accurately labels what was already happening.
  // The PlacementsSection radio still lets users opt into Manual mode,
  // which atomically populates publisher_platforms to a non-empty
  // default ('facebook', 'instagram') and clears the flag.
  advantage_placements: true,
});

const defaultPromotedObject = (): MetaPromotedObject => ({});

const isAdSetWizardObjective = (
  o: string | null | undefined,
): o is MetaCampaignObjective =>
  o === 'OUTCOME_SALES' ||
  o === 'OUTCOME_TRAFFIC' ||
  o === 'OUTCOME_AWARENESS' ||
  o === 'OUTCOME_ENGAGEMENT' ||
  o === 'OUTCOME_LEADS' ||
  o === 'OUTCOME_APP_PROMOTION';

export const CreateAdSetWizard: React.FC<CreateAdSetWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  parentCampaign,
}) => {
  // Look up the stored Facebook Page ID for this ad account from the
  // auth store. Populated by `preloadLinkedAccounts` on login. When
  // present, the LEADS path's PromotedObject step renders just that
  // one Page as a read-only informational card instead of the full
  // selectable Page list (avoids forcing the user to re-pick the Page
  // they already committed to at link time).
  const linkedMetaAccountDetails = useAuthStore(
    (s) => s.linkedMetaAccountDetails,
  );
  const memberUuid = useAuthStore((s) => s.memberUuid);
  const queryClient = useQueryClient();
  const storedPageId = useMemo(() => {
    const acc = linkedMetaAccountDetails.find(
      (a) => a.id === (parentCampaign.meta_ad_account_id ?? ''),
    );
    // Normalize a stored blank string to null. Downstream consumers
    // (validateStep, submit, AdSetPromotedObjectStep) all check
    // `storedPageId === null` to decide whether to surface the
    // page-picker UI; treating `''` as "no default" keeps that
    // contract honest and avoids the "no picker, no auto-fill"
    // dead-state the LEADS path would otherwise fall into.
    const raw = acc?.pageId;
    return raw && raw.length > 0 ? raw : null;
  }, [linkedMetaAccountDetails, parentCampaign.meta_ad_account_id]);

  // Memoize today YYYY-MM-DD per open/close cycle so users editing across
  // midnight don't trip a reset. Same convention as CreateMetaCampaignWizard.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => formatDateForInput(new Date()), [isOpen]);

  // Resolve parent-derived context once per parent change.
  const parentObjective: MetaCampaignObjective = useMemo(() => {
    if (isAdSetWizardObjective(parentCampaign.objective)) {
      return parentCampaign.objective;
    }
    // Defensive fallback — shouldn't hit if backend invariants hold.
    return 'OUTCOME_TRAFFIC';
  }, [parentCampaign.objective]);

  const parentBidStrategy = parentCampaign.bid_strategy ?? null;
  const parentSpecialAdCategories = (parentCampaign.special_ad_categories ?? [])
    .filter((c): c is MetaSpecialAdCategory =>
      c === 'NONE' ||
      c === 'HOUSING' ||
      c === 'EMPLOYMENT' ||
      c === 'CREDIT' ||
      c === 'ISSUES_ELECTIONS_POLITICS',
    );
  const adAccountId = parentCampaign.meta_ad_account_id ?? '';
  // CBO (Campaign Budget Optimization) — when the parent campaign
  // carries its own daily/lifetime budget, Meta requires ad sets under
  // it to NOT carry their own budget. The wizard skips the Budget step
  // entirely in that case and suppresses budget fields on the payload.
  const parentIsCbo =
    (parentCampaign.daily_budget ?? null) !== null ||
    (parentCampaign.lifetime_budget ?? null) !== null;

  // CBO `optimization_goal` inheritance: Meta requires all ad sets in a
  // CBO campaign to share the same `optimization_goal` (error message:
  // "Optimization for Ad Delivery Selections Must Be the Same").
  // When the parent campaign already has at least one ad set, read its
  // optimization_goal and lock the wizard's dropdown to that value.
  // Empty case (first ad set under this campaign) leaves the dropdown
  // open — the user's pick becomes the lock for future ad sets.
  const inheritedOptimizationGoal = useMemo<MetaOptimizationGoal | null>(() => {
    if (!parentIsCbo) return null;
    const siblings = parentCampaign.ad_sets ?? [];
    for (const sibling of siblings) {
      const g = sibling.optimization_goal;
      if (g) return g as MetaOptimizationGoal;
    }
    return null;
  }, [parentIsCbo, parentCampaign.ad_sets]);

  const requiresPromotedObject =
    OBJECTIVES_REQUIRING_PROMOTED_OBJECT.has(parentObjective);
  const showBidAmount =
    parentBidStrategy !== null &&
    // Source is `string` from the unified Campaign type, but the set is
    // typed to `MetaBidStrategy` so typos in the set body get caught at
    // compile time. The narrowing cast here is safe: `Set.has` is just
    // a string lookup at runtime, and the union typing of the set is
    // the load-bearing piece.
    CONSTRAINED_BID_STRATEGIES.has(parentBidStrategy as MetaBidStrategy);
  const ageLocked = parentSpecialAdCategories.some((c) =>
    AGE_RESTRICTED_CATEGORIES.has(c),
  );

  // Default optimization_goal for the parent objective, and default
  // billing_event from the matrix. The matrix lists IMPRESSIONS first
  // for every multi-option goal (see the long-form comment on
  // `OPTIMIZATION_GOAL_TO_BILLING_EVENTS` in metaAdSetMatrix.ts), so
  // the default falls on the universally-accepted billing event by
  // construction — no per-scenario special case needed here.
  const defaultGoal: MetaOptimizationGoal =
    OBJECTIVE_TO_OPTIMIZATION_GOALS[parentObjective][0] ?? 'LINK_CLICKS';
  const defaultBilling: MetaBillingEvent =
    OPTIMIZATION_GOAL_TO_BILLING_EVENTS[defaultGoal][0] ?? 'IMPRESSIONS';
  // `isFirstAdSetInCbo` still drives the warning Alert in
  // AdSetBasicsStep (the lock-in implication is independent of the
  // billing default).
  const isFirstAdSetInCbo = parentIsCbo && inheritedOptimizationGoal === null;

  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Form state
  const [adSetName, setAdSetName] = useState('');
  const [optimizationGoal, setOptimizationGoal] =
    useState<MetaOptimizationGoal>(defaultGoal);
  const [billingEvent, setBillingEvent] =
    useState<MetaBillingEvent>(defaultBilling);
  const [status, setStatus] = useState<MetaAdSetCreateStatus>('PAUSED');
  const [budgetMode, setBudgetMode] = useState<MetaBudgetMode>('daily');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [hasEndDate, setHasEndDate] = useState(false);
  const [targeting, setTargeting] = useState<MetaTargetingSpec>(defaultTargeting);
  const [promotedObject, setPromotedObject] =
    useState<MetaPromotedObject>(defaultPromotedObject);

  // Whenever the user picks a new optimization goal, snap billing event
  // back to the first compatible value (per the matrix). Prevents an
  // incompatible pair being shipped from a previous selection.
  useEffect(() => {
    const allowed = OPTIMIZATION_GOAL_TO_BILLING_EVENTS[optimizationGoal] ?? [];
    if (allowed.length > 0 && !allowed.includes(billingEvent)) {
      setBillingEvent(allowed[0]);
    }
  }, [optimizationGoal, billingEvent]);

  // Reset state on open. Re-derive defaults from the parent each time so
  // a launch from a different campaign starts fresh.
  useEffect(() => {
    if (isOpen) {
      // Prefer the inherited optimization_goal (CBO + sibling ad sets);
      // fall back to the objective-default for first-ad-set + ABO cases.
      const goal =
        inheritedOptimizationGoal ??
        OBJECTIVE_TO_OPTIMIZATION_GOALS[parentObjective][0] ??
        'LINK_CLICKS';
      // Matrix-first default (matrix lists IMPRESSIONS first wherever
      // allowed, so this naturally lands on the safe option).
      const billing: MetaBillingEvent =
        OPTIMIZATION_GOAL_TO_BILLING_EVENTS[goal][0] ?? 'IMPRESSIONS';
      setCurrentStep(1);
      setIsSubmitting(false);
      setSubmitError(null);
      setErrors({});
      setAdSetName('');
      setOptimizationGoal(goal);
      setBillingEvent(billing);
      setStatus('PAUSED');
      setBudgetMode('daily');
      setBudgetAmount('');
      setBidAmount('');
      setStartDate(today);
      setEndDate('');
      setHasEndDate(false);
      setTargeting({
        ...defaultTargeting(),
        // Lock age_min to 18 when the parent has a regulated category.
        age_min: ageLocked ? 18 : 18,
      });
      setPromotedObject(defaultPromotedObject());
    }
  }, [isOpen, parentObjective, ageLocked, today, inheritedOptimizationGoal]);

  const defaultTargetingForDirtyCheck = defaultTargeting();
  const targetingIsDefault =
    targeting.geo_locations.countries.length === 1 &&
    targeting.geo_locations.countries[0] === 'US' &&
    targeting.age_min === defaultTargetingForDirtyCheck.age_min &&
    targeting.age_max === defaultTargetingForDirtyCheck.age_max &&
    targeting.genders.length === 2 &&
    targeting.interests.length === 0 &&
    targeting.publisher_platforms.length === 0 &&
    targeting.facebook_positions.length === 0 &&
    targeting.instagram_positions.length === 0 &&
    targeting.device_platforms.length === 0 &&
    targeting.locales.length === 0 &&
    targeting.advantage_audience === false &&
    targeting.advantage_placements === true;
  const promotedObjectIsDefault =
    Object.values(promotedObject).every((v) => v === undefined || v === '');

  // When the parent is CBO + has sibling ad sets, the open-time default
  // for optimization_goal/billing_event is the inherited value, not the
  // objective-default. Comparing against the actual initial value keeps
  // a CBO-inherited wizard from registering as "dirty" the instant it
  // opens (which would trigger the discard-confirm on an immediate close).
  const initialGoal: MetaOptimizationGoal =
    inheritedOptimizationGoal ?? defaultGoal;
  const initialBilling: MetaBillingEvent =
    OPTIMIZATION_GOAL_TO_BILLING_EVENTS[initialGoal][0] ?? defaultBilling;
  const isDirty =
    adSetName !== '' ||
    optimizationGoal !== initialGoal ||
    billingEvent !== initialBilling ||
    status !== 'PAUSED' ||
    budgetMode !== 'daily' ||
    budgetAmount !== '' ||
    bidAmount !== '' ||
    startDate !== today ||
    endDate !== '' ||
    hasEndDate !== false ||
    !targetingIsDefault ||
    !promotedObjectIsDefault;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!adSetName.trim()) {
        newErrors.name = 'Ad set name is required.';
      } else if (adSetName.length > 400) {
        newErrors.name = 'Name must be 400 characters or fewer.';
      } else if (!NAME_PATTERN.test(adSetName)) {
        newErrors.name = 'Name cannot contain control characters.';
      }
      const allowedGoals = OBJECTIVE_TO_OPTIMIZATION_GOALS[parentObjective] ?? [];
      if (!allowedGoals.includes(optimizationGoal)) {
        newErrors.optimizationGoal =
          `optimization goal ${optimizationGoal} not allowed for parent objective ${parentObjective}.`;
      }
      const allowedBilling = OPTIMIZATION_GOAL_TO_BILLING_EVENTS[optimizationGoal] ?? [];
      if (!allowedBilling.includes(billingEvent)) {
        newErrors.billingEvent =
          `billing event ${billingEvent} is not compatible with goal ${optimizationGoal}.`;
      }
    }

    if (step === 2) {
      // Budget validation only when parent is non-CBO (CBO holds the
      // budget at the campaign level and the payload omits it).
      if (!parentIsCbo) {
        const parsed = parseFloat(budgetAmount);
        if (!budgetAmount || Number.isNaN(parsed) || parsed <= 0) {
          newErrors.amount = 'Budget must be a positive amount.';
        }
      }
      // Bid amount is required for constrained bid strategies regardless
      // of CBO state — Meta enforces this at the ad-set level.
      if (showBidAmount) {
        const parsedBid = parseFloat(bidAmount);
        if (!bidAmount || Number.isNaN(parsedBid) || parsedBid <= 0) {
          newErrors.bidAmount =
            `Bid amount is required because the parent campaign uses ${parentBidStrategy}.`;
        }
      }
    }

    if (step === 3) {
      if (!startDate) {
        newErrors.startDate = 'Start date is required.';
      } else if (startDate < today) {
        newErrors.startDate = 'Start date cannot be in the past.';
      }
      const endExpected = budgetMode === 'lifetime' || hasEndDate;
      if (endExpected && !endDate) {
        newErrors.endDate =
          budgetMode === 'lifetime'
            ? 'End date is required when using a lifetime budget.'
            : 'End date is required when "Set an end date" is on.';
      }
      if (endExpected && startDate && endDate && endDate <= startDate) {
        newErrors.endDate = 'End date must be after the start date.';
      }
    }

    if (step === 4) {
      if (targeting.geo_locations.countries.length === 0) {
        newErrors.countries = 'At least one country is required.';
      }
      // Age 18+ lock for regulated categories (UI mirrors backend's
      // fail-closed default).
      if (ageLocked && targeting.age_min < 18) {
        newErrors.age =
          'Age minimum must be 18+ when the parent campaign has a regulated category.';
      }
      if (targeting.age_min > targeting.age_max) {
        newErrors.age = 'Age minimum cannot exceed maximum.';
      }
    }

    if (step === 5 && requiresPromotedObject) {
      if (parentObjective === 'OUTCOME_SALES') {
        if (!promotedObject.pixel_id) {
          newErrors.pixel = 'Pixel is required for Sales campaigns.';
        }
        if (!promotedObject.custom_event_type) {
          newErrors.customEventType = 'Conversion event is required.';
        }
      } else if (parentObjective === 'OUTCOME_LEADS') {
        // Effective page = user pick OR stored default from
        // meta_ads_credentials.page_id (auth store). The lead form
        // itself is NOT picked here — Meta's promoted_object for LEADS
        // only takes page_id; the form_id belongs on the ad creative
        // (call_to_action.value.lead_gen_form_id) and is selected in
        // the ad creative wizard.
        const effectivePageId = promotedObject.page_id ?? storedPageId ?? '';
        if (!effectivePageId) {
          newErrors.page = 'Page is required for Lead campaigns.';
        }
      } else if (parentObjective === 'OUTCOME_APP_PROMOTION') {
        if (!promotedObject.application_id) {
          newErrors.applicationId = 'App ID is required.';
        }
        if (
          !promotedObject.object_store_url ||
          !/^https?:\/\//.test(promotedObject.object_store_url)
        ) {
          newErrors.objectStoreUrl = 'App Store URL must start with http(s)://';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step nav — skip the Budget step (step 2) when the parent campaign
  // uses CBO AND the parent's bid_strategy doesn't require a per-ad-set
  // bid_amount. CBO + constrained bid_strategy (BID_CAP / COST_CAP /
  // MIN_ROAS) still needs the bid_amount input — the step renders but
  // hides the budget controls in that case. Skip the PromotedObject
  // step (step 5) when the parent objective doesn't require it.
  const stepShouldRender = (step: number): boolean => {
    if (step === 2) return !parentIsCbo || showBidAmount;
    if (step === 5) return requiresPromotedObject;
    return true;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    let next = currentStep + 1;
    while (next < ALL_WIZARD_STEPS.length && !stepShouldRender(next)) {
      next += 1;
    }
    setCurrentStep(Math.min(next, ALL_WIZARD_STEPS.length));
  };

  const handlePrevious = () => {
    let prev = currentStep - 1;
    while (prev > 1 && !stepShouldRender(prev)) {
      prev -= 1;
    }
    setCurrentStep(Math.max(prev, 1));
  };

  const handleStepClick = (step: number) => {
    if (step < currentStep && step >= 1 && stepShouldRender(step)) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    // Re-run every visible step's validators.
    for (let step = 1; step <= 5; step++) {
      if (!stepShouldRender(step)) continue;
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    if (!adAccountId) {
      setSubmitError('Parent campaign is missing meta_ad_account_id.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload: CreateMetaAdSetRequest = {
        meta_ad_account_id: adAccountId,
        ad_set_name: adSetName.trim(),
        optimization_goal: optimizationGoal,
        billing_event: billingEvent,
        status,
        start_time: startDate,
        targeting,
      };
      // Under CBO the parent campaign carries the budget; the ad-set
      // payload omits both budget keys entirely (Meta rejects with
      // "Can't Set Ad Set and Campaign Budget" otherwise).
      if (!parentIsCbo) {
        const amount = parseFloat(budgetAmount);
        if (budgetMode === 'daily') {
          payload.daily_budget = amount;
        } else {
          payload.lifetime_budget = amount;
        }
      }
      // Bid amount is ad-set-scoped regardless of CBO — required for
      // constrained bid strategies, dropped otherwise.
      if (showBidAmount && bidAmount) {
        payload.bid_amount = parseFloat(bidAmount);
      }
      // End-time is required for lifetime ad-set budgets; CBO has no
      // ad-set budget mode, so end-time depends only on the user toggle.
      const endExpected = (!parentIsCbo && budgetMode === 'lifetime') || hasEndDate;
      if (endExpected && endDate) {
        payload.end_time = endDate;
      }
      if (requiresPromotedObject) {
        // Build the promoted_object payload as the **single shape**
        // matched to the parent objective. Mixing fields across shapes
        // (e.g. a stray `page_id` under a SALES parent because the
        // auth store has a `storedPageId`) causes Meta validation
        // failures, and the backend model + router both now reject
        // mixed shapes outright.
        const cleaned: MetaPromotedObject = {};
        if (parentObjective === 'OUTCOME_SALES') {
          if (promotedObject.pixel_id) cleaned.pixel_id = promotedObject.pixel_id;
          if (promotedObject.custom_event_type)
            cleaned.custom_event_type = promotedObject.custom_event_type;
        } else if (parentObjective === 'OUTCOME_LEADS') {
          // For LEADS, the picker silently uses `storedPageId` from
          // the auth store when the user didn't explicitly pick a page
          // (see `AdSetPromotedObjectStep`'s LeadsSubForm
          // `effectivePageId`). Apply the fallback here too. Meta's
          // promoted_object schema for LEADS only takes page_id — the
          // form_id is selected later on the ad creative.
          const effectivePageId = promotedObject.page_id ?? storedPageId ?? '';
          if (effectivePageId) cleaned.page_id = effectivePageId;
        } else if (parentObjective === 'OUTCOME_APP_PROMOTION') {
          if (promotedObject.application_id)
            cleaned.application_id = promotedObject.application_id;
          if (promotedObject.object_store_url)
            cleaned.object_store_url = promotedObject.object_store_url;
        }
        payload.promoted_object = cleaned;
      }

      // For LEADS ad sets we always want the instant-form destination
      // (`ON_AD`). Meta rejects creatives with `lead_gen_form_id` when
      // the parent ad set's `destination_type` is anything else (defaults
      // to WEBSITE for LEADS if omitted, which then makes the ad-create
      // 400 with "Creative with lead form can only be used for Lead
      // Generation objective and ON_AD destination"). Non-instant-form
      // LEADS variants (MESSENGER / INSTAGRAM_DIRECT / PHONE_CALL) aren't
      // yet a Flouri surface — when they are, expose a picker here.
      if (parentObjective === 'OUTCOME_LEADS') {
        payload.destination_type = 'ON_AD';
      }

      debug.log('CreateAdSetWizard: Submitting', payload);
      const response = await metaAdsApi.createAdSet(campaignId, payload);
      debug.log('CreateAdSetWizard: Created', response);

      // Optimistically splice the new ad set into the app-config cache so the
      // campaign tree renders it immediately, regardless of whether the
      // backend's own app_configuration write has propagated yet. Mirrors
      // Google's CreateAdGroupWizard pattern. See
      // docs/FRONTEND_MUTATION_PATTERNS.md for the "why".
      //
      // We use the server-authoritative `applied_entity` snapshot so the
      // shape matches what the backend committed to `app_configuration`
      // exactly — no risk of the frontend reconstructing a divergent shape.
      if (memberUuid && response.applied_entity) {
        // Backend passes through the raw dict for shape fidelity; cast to
        // the local MetaAdSet type at the splice boundary.
        const appliedAdSet = response.applied_entity as unknown as import('@/types/metaCampaign').MetaAdSet;
        queryClient.setQueryData<AppConfiguration>(
          ['app-config', memberUuid],
          (prev) => appendOptimisticMetaAdSet(prev, campaignId, appliedAdSet),
        );
      }
      // Don't refetch ['app-config'] — that's the source of the race the
      // Meta wizards used to hit. Invalidate the derived campaign-from-cache
      // so it re-derives from our just-updated app-config.
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
    } catch (error: unknown) {
      debug.error('CreateAdSetWizard: Submit failed', error);
      let message = 'Failed to create ad set. Please try again.';
      const apiError = error as {
        response?: {
          data?: { detail?: string | unknown; message?: string };
        };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        const detail = data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (detail) {
          message = JSON.stringify(detail);
        } else if (data.message) {
          message = data.message;
        }
      } else if (apiError.message) {
        message = apiError.message;
      }
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Determine which step labels to show — drop the PromotedObject label
  // when it's not required so the ProgressIndicator's totalSteps stays
  // honest.
  const visibleSteps = ALL_WIZARD_STEPS.filter((_, idx) => {
    const stepNumber = idx + 1;
    return stepShouldRender(stepNumber);
  });
  const totalSteps = ALL_WIZARD_STEPS.length;
  // Translate the current step number to the visible-step ordinal for
  // ProgressIndicator (it doesn't know about hidden steps).
  const currentVisibleStep =
    ALL_WIZARD_STEPS.slice(0, currentStep).filter((_, idx) =>
      stepShouldRender(idx + 1),
    ).length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new Meta ad set"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-4 sm:my-8 relative">
        <button
          onClick={handleRequestClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close wizard"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">
            Create New Ad Set
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new ad set to {parentCampaign.campaign_name}
          </p>
        </div>

        <div className="px-4 sm:px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentVisibleStep}
            totalSteps={visibleSteps.length}
            steps={visibleSteps}
            onStepClick={(visibleIdx) => {
              // Translate visible-step ordinal back to actual step number.
              let count = 0;
              for (let i = 1; i <= totalSteps; i++) {
                if (stepShouldRender(i)) {
                  count += 1;
                  if (count === visibleIdx) {
                    handleStepClick(i);
                    return;
                  }
                }
              }
            }}
          />
        </div>

        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {currentStep === 1 && (
            <AdSetBasicsStep
              parentObjective={parentObjective}
              adSetName={adSetName}
              optimizationGoal={optimizationGoal}
              billingEvent={billingEvent}
              status={status}
              inheritedOptimizationGoal={inheritedOptimizationGoal}
              isFirstAdSetInCbo={isFirstAdSetInCbo}
              onChangeName={setAdSetName}
              onChangeOptimizationGoal={setOptimizationGoal}
              onChangeBillingEvent={setBillingEvent}
              onChangeStatus={setStatus}
              errors={{
                name: errors.name,
                optimizationGoal: errors.optimizationGoal,
                billingEvent: errors.billingEvent,
              }}
            />
          )}

          {currentStep === 2 && (
            <AdSetBudgetStep
              parentBidStrategy={parentBidStrategy}
              parentIsCbo={parentIsCbo}
              budgetMode={budgetMode}
              budgetAmount={budgetAmount}
              bidAmount={bidAmount}
              onChangeMode={setBudgetMode}
              onChangeAmount={setBudgetAmount}
              onChangeBidAmount={setBidAmount}
              errors={{
                amount: errors.amount,
                bidAmount: errors.bidAmount,
              }}
            />
          )}

          {currentStep === 3 && (
            <AdSetScheduleStep
              budgetMode={budgetMode}
              startDate={startDate}
              endDate={endDate}
              hasEndDate={hasEndDate}
              today={today}
              onChangeStartDate={setStartDate}
              onChangeEndDate={setEndDate}
              onChangeHasEndDate={setHasEndDate}
              errors={{
                startDate: errors.startDate,
                endDate: errors.endDate,
              }}
            />
          )}

          {currentStep === 4 && (
            <TargetingStep
              parentSpecialAdCategories={parentSpecialAdCategories}
              adAccountId={adAccountId}
              targeting={targeting}
              onChangeTargeting={setTargeting}
              errors={{
                countries: errors.countries,
                age: errors.age,
              }}
            />
          )}

          {currentStep === 5 && requiresPromotedObject && (
            <AdSetPromotedObjectStep
              parentObjective={parentObjective}
              adAccountId={adAccountId}
              storedPageId={storedPageId}
              promotedObject={promotedObject}
              onChange={setPromotedObject}
              errors={{
                pixel: errors.pixel,
                customEventType: errors.customEventType,
                page: errors.page,
                applicationId: errors.applicationId,
                objectStoreUrl: errors.objectStoreUrl,
              }}
            />
          )}

          {currentStep === 6 && (
            <AdSetReviewStep
              parentObjective={parentObjective}
              parentCampaignName={parentCampaign.campaign_name}
              parentBidStrategy={parentBidStrategy}
              adSetName={adSetName}
              optimizationGoal={optimizationGoal}
              billingEvent={billingEvent}
              status={status}
              budgetMode={budgetMode}
              budgetAmount={budgetAmount}
              bidAmount={bidAmount}
              showBidAmount={showBidAmount}
              parentIsCbo={parentIsCbo}
              startDate={startDate}
              endDate={endDate}
              hasEndDate={hasEndDate}
              targeting={targeting}
              promotedObject={
                requiresPromotedObject
                  ? // Mirror the submit-path fallback (~line 575): when the
                    // LEADS ad-set wizard renders the read-only PageCard
                    // (account has a stored default Page), the user never
                    // explicitly sets `promotedObject.page_id`, but the
                    // serializer fills it in from `storedPageId` on submit.
                    // Without this same fallback here, Review would render
                    // `—` for the Page summary while the submit actually
                    // posts the stored ID — silently divergent.
                    parentObjective === 'OUTCOME_LEADS' &&
                    !promotedObject.page_id &&
                    storedPageId
                    ? { ...promotedObject, page_id: storedPageId }
                    : promotedObject
                  : null
              }
              adAccountId={adAccountId}
              onEditStep={handleStepClick}
            />
          )}

        </div>

        {/* Desktop footer — error banner sits above the button row so a
            submit failure is always visible even on long steps (Targeting
            scrolls far below the fold). Mirrors CreateAdGroupWizard. */}
        <div className="hidden sm:block px-6 py-4 border-t border-gray-200">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p role="alert" className="text-sm text-red-800">
                {linkifyUrls(submitError)}
              </p>
            </div>
          )}
          <div className="flex justify-between items-center">
            <Button
              onClick={handlePrevious}
              disabled={isSubmitting || currentStep <= 1}
              variant="secondary"
            >
              Previous
            </Button>
            <div className="text-sm text-gray-500">
              Step {currentVisibleStep} of {visibleSteps.length}
            </div>
            {currentStep < ALL_WIZARD_STEPS.length ? (
              <Button onClick={handleNext} disabled={isSubmitting}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="primary"
              >
                {isSubmitting ? 'Creating Ad Set...' : 'Create Ad Set'}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile footer — same pattern. The `block sm:hidden` /
            `hidden sm:block` split ensures only one banner renders at a
            time per breakpoint, so screen readers don't double-announce. */}
        <div className="block sm:hidden px-6 py-4 border-t border-gray-200">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p role="alert" className="text-sm text-red-800">
                {linkifyUrls(submitError)}
              </p>
            </div>
          )}
          <div className="flex flex-col items-center gap-3">
            {currentStep < ALL_WIZARD_STEPS.length ? (
              <Button onClick={handleNext} disabled={isSubmitting} className="w-full">
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="primary"
                className="w-full"
              >
                {isSubmitting ? 'Creating Ad Set...' : 'Create Ad Set'}
              </Button>
            )}
            <div className="text-sm text-gray-500">
              Step {currentVisibleStep} of {visibleSteps.length}
            </div>
            <Button
              onClick={handlePrevious}
              disabled={isSubmitting || currentStep <= 1}
              variant="secondary"
              className="w-full"
            >
              Previous
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Discard ad set?"
        message="You have unsaved changes. Closing now will discard the ad set you've been setting up."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />
    </div>
  );
};
